/**
 * O que há para lançar, agrupado como o lançamento de fato acontece.
 *
 * A lista de protocolos, sozinha, não deixa óbvio que **cada arquivo é um
 * evento de baixa fechado**. Quando o demonstrativo de março chega, o que se
 * lança é o que veio nele — o que veio em fevereiro já foi lançado, mesmo
 * sendo do mesmo protocolo. Ler um acumulado como se fosse o valor do mês é o
 * caminho mais curto para baixar duas vezes o mesmo dinheiro.
 *
 * Por isso a competência é o nível de cima e o arquivo é o de baixo: é assim
 * que quem lança pensa ("o que entrou em março?").
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mensagemErro } from '../api/axios';
import { visaoBaixas } from '../api/fluxfin';
import { Ajuda, Alerta, LoadingSpinner, Painel, Vazio } from './common';
import { brl, formatDate, numero } from '../utils/formato';

export default function BaixasPorCompetencia() {
  const navigate = useNavigate();
  const [grupos, setGrupos] = useState([]);
  const [abertos, setAbertos] = useState({});
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    visaoBaixas()
      .then((r) => {
        setGrupos(r.data);
        // A competência mais recente já vem aberta: é a que está para lançar.
        if (r.data.length > 0) setAbertos({ [r.data[0].competencia]: true });
      })
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível carregar as baixas.')))
      .finally(() => setCarregando(false));
  }, []);

  const alternar = (comp) => setAbertos((a) => ({ ...a, [comp]: !a[comp] }));

  if (carregando) return <LoadingSpinner />;

  return (
    <Painel
      titulo="Baixas a lançar"
      subtitulo="Por competência e por arquivo — cada arquivo é um lançamento próprio."
    >
      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>

      {grupos.length === 0 ? (
        <Vazio
          icone="bi-cash-stack"
          titulo="Nada conciliado ainda"
          descricao="Assim que um arquivo confirmado tiver suas guias resolvidas no Bioslab, o que há para baixar aparece aqui."
        />
      ) : (
        <div className="p-3 d-flex flex-column gap-2">
          {grupos.map((g) => {
            const aberto = !!abertos[g.competencia];
            return (
              <div
                key={g.competencia}
                style={{
                  border: '1px solid var(--ff-border-soft)',
                  borderRadius: 12,
                  overflow: 'hidden',
                }}
              >
                <button
                  type="button"
                  className="w-100 d-flex align-items-center gap-3 p-3"
                  onClick={() => alternar(g.competencia)}
                  aria-expanded={aberto}
                  style={{
                    background: 'var(--ff-surface-2)',
                    border: 0,
                    textAlign: 'left',
                  }}
                >
                  <i
                    className={`bi bi-chevron-${aberto ? 'down' : 'right'}`}
                    aria-hidden="true"
                  />
                  <div style={{ flex: 1 }}>
                    <div className="ff-mono" style={{ fontWeight: 600 }}>
                      {g.competencia}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
                      {numero(g.qtd_arquivos)} arquivo{g.qtd_arquivos === 1 ? '' : 's'} ·{' '}
                      {numero(g.qtd_protocolos)} protocolo
                      {g.qtd_protocolos === 1 ? '' : 's'} · {numero(g.qtd_guias)} guias
                    </div>
                  </div>
                  <div className="ff-mono" style={{ fontWeight: 600, fontSize: 16 }}>
                    {brl(g.valor)}
                  </div>
                </button>

                {aberto && (
                  <div className="p-3 d-flex flex-column gap-2">
                    {g.arquivos.map((a) => (
                      <button
                        key={a.id_arquivo}
                        type="button"
                        className="w-100 d-flex align-items-center gap-3 px-3 py-2"
                        onClick={() => navigate(`/protocolos?arquivo=${a.id_arquivo}`)}
                        title="Ver o que este arquivo manda baixar, protocolo a protocolo"
                        style={{
                          background: 'transparent',
                          border: '1px solid var(--ff-border-soft)',
                          borderRadius: 10,
                          textAlign: 'left',
                        }}
                      >
                        <i className="bi bi-file-earmark-text" aria-hidden="true" />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="ff-mono" style={{ fontSize: 13 }}>
                            {a.arquivo}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
                            {a.operadora} · {numero(a.qtd_protocolos)} protocolo
                            {a.qtd_protocolos === 1 ? '' : 's'} · recebido em{' '}
                            {formatDate(a.recebido_em)}
                          </div>
                        </div>
                        <div className="ff-mono" style={{ fontWeight: 600 }}>
                          {brl(a.valor)}
                        </div>
                        <i className="bi bi-chevron-right" aria-hidden="true" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          <p style={{ fontSize: 12, color: 'var(--ff-muted)', margin: '4px 2px 0' }}>
            <Ajuda titulo="Por que separado por arquivo" origem="Regra de negócio">
              Um protocolo recebe ao longo do tempo. Somar tudo num número só faria a
              baixa do mês incluir o que já foi lançado em meses anteriores — baixa em
              duplicidade. Cada arquivo é um lançamento fechado.
            </Ajuda>
            Cada arquivo é um lançamento fechado. O mesmo protocolo pode aparecer em
            competências diferentes, uma vez por recebimento.
          </p>
        </div>
      )}
    </Painel>
  );
}
